import { useEffect, useRef, useState } from 'react';

import type {
  ProductSelected,
  UpgradeTrackingProperties,
} from 'app/hooks/useSubscriptionPlans/selectNewTierPlan';
import { trackJusGPTLimitDismissed } from 'app/pages/JusGPT/helpers/trackJusGPTEvents';

import { ExplorePlansModal } from '../ExplorePlansModal';
import { ComparePlansModal } from '../ComparePlansModal';
import { CheckoutModal } from '../CheckoutModal';
import type { PlanExplorationModalProps } from './types';

type Screen = 'explore' | 'compare' | 'checkout';
type PlansScreen = Exclude<Screen, 'checkout'>;

// Local 6a⇄6c⇄checkout navigation — separate from legacy useSubscriptionModal.screen.
// Selected plan stays in local state (not shared PlanUpgradeModal formik) so the legacy
// screen machine does not jump to the old checkout.
export function PlanExplorationModal({
  isOpen,
  onClose,
  user,
  subscriptionStatus,
  url,
  origin,
  exploreHeadline,
  jusgptLimitFunnel,
  initialProductSelected,
}: PlanExplorationModalProps) {
  const [screen, setScreen] = useState<Screen>(() =>
    isOpen && initialProductSelected ? 'checkout' : 'explore',
  );
  const [checkoutOrigin, setCheckoutOrigin] = useState<PlansScreen>('explore');
  const [productSelected, setProductSelected] = useState<ProductSelected | null>(
    () => (isOpen && initialProductSelected ? initialProductSelected : null),
  );
  // catálogo/convênio/tier/modo da seleção, gravados por selectNewTierPlan.ts
  // via formik separado de product_selected (esse é o payload literal do POST /subscription/create).
  const [trackingProperties, setTrackingProperties] = useState<UpgradeTrackingProperties | null>(
    null,
  );
  const hasConvertedRef = useRef(false);

  const prevIsOpenRef = useRef(isOpen);

  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (!wasOpen && isOpen && initialProductSelected) {
      setProductSelected(initialProductSelected);
      setCheckoutOrigin('explore');
      setScreen('checkout');
    }

    if (wasOpen && !isOpen) {
      setScreen('explore');
      setProductSelected(null);
      setTrackingProperties(null);
      hasConvertedRef.current = false;
    }
  }, [isOpen, initialProductSelected]);

  const goToCheckout = (originScreen: PlansScreen) => () => {
    setCheckoutOrigin(originScreen);
    setScreen('checkout');
  };

  const handleClose = () => {
    if (jusgptLimitFunnel && !hasConvertedRef.current) {
      trackJusGPTLimitDismissed({ plan: jusgptLimitFunnel.plan });
    }
    onClose();
  };

  if (screen === 'checkout' && productSelected) {
    return (
      <CheckoutModal
        isOpen={isOpen}
        onClose={handleClose}
        onBack={() => setScreen(checkoutOrigin)}
        productSelected={productSelected}
        trackingProperties={trackingProperties}
        jusgptLimitFunnel={jusgptLimitFunnel}
        onCheckoutSuccess={() => {
          hasConvertedRef.current = true;
        }}
      />
    );
  }

  const localFormik = {
    setFieldValue: (field: string, value: unknown) => {
      if (field === 'product_selected') {
        setProductSelected(value as ProductSelected | null);
      }
      if (field === 'upgrade_tracking_properties') {
        setTrackingProperties(value as UpgradeTrackingProperties | null);
      }
    },
  };

  const sharedProps = {
    isOpen,
    onClose: handleClose,
    formik: localFormik,
    user,
    subscriptionStatus,
    url,
  };

  if (screen === 'compare') {
    return (
      <ComparePlansModal
        {...sharedProps}
        selectPlan={goToCheckout('compare')}
        onBackToExplore={() => setScreen('explore')}
      />
    );
  }

  return (
    <ExplorePlansModal
      {...sharedProps}
      selectPlan={goToCheckout('explore')}
      onCompareAllPlans={() => setScreen('compare')}
      origin={origin}
      headline={exploreHeadline}
    />
  );
}

export default PlanExplorationModal;
